/**
 * `Keymap` — the data structure mapping actions to the chords that trigger
 * them, plus the derived reverse index and conflict detection (#64).
 *
 * A `Keymap` is `GameAction → KeyChord[]` (multiple bindings per action
 * allowed). For dispatch we build a `chordId → GameAction` REVERSE INDEX once,
 * giving O(1) lookup in the hot key-event path. Conflict detection (two
 * actions bound to the same chord) is a pure function over the keymap.
 *
 * DETERMINISM OF ITERATION: entries are kept in a stable array (definition
 * order); the reverse index is built by walking that order. When two chords
 * collide, "first entry wins" is deterministic — and surfaced by
 * `findConflicts` so a customization UI can warn.
 *
 * The keymap references ONLY `GameAction`s (slots + static actions). It encodes
 * no ability identity and no champion knowledge — that is what keeps
 * config-authored content drivable by the default keymap with zero code change.
 */

import { toAbilitySlot } from "@pit/protocol";
import type { GameAction } from "./actions.js";
import { gameActionId } from "./actions.js";
import type { KeyChord } from "./chord.js";
import { bareChord, chordId, chordLabel, normalizeChord } from "./chord.js";

/** One action and the chords bound to it. */
export interface KeyBinding {
  readonly action: GameAction;
  readonly chords: readonly KeyChord[];
}

/** A full keymap: an ordered list of action→chords bindings. */
export interface Keymap {
  readonly bindings: readonly KeyBinding[];
}

/**
 * Schema version for the persisted keymap (prefs). Bump when the serialized
 * shape changes so old stored keymaps can be detected + merged forward rather
 * than mis-read. v1.
 */
export const KEYMAP_VERSION = 1 as const;

/**
 * Number of ability slots the default keymap binds — 1..8 → slots 0..7 (the
 * numeral row), plus q/e/r/` → slots 8..11 (#407, the action bar's #322
 * 12-slot expansion never got a matching default-bind update until now).
 */
const DEFAULT_CAST_SLOTS = 12 as const;

/**
 * Default chords for the slots numeral keys can't reach — there is no single
 * "9"/"10"/"11"/"12" key on a standard keyboard, so slots 8..11 (ability slots
 * 9-12) get a 4-key cluster instead (#407). `q`/`e`/`r` sit either side of the
 * WASD movement cluster's `w` (which stays reserved for "move forward" and is
 * deliberately skipped); `` ` `` (backtick) sits immediately left of "1",
 * extending the reachable cluster around the numeral row. None of the four
 * collide with any other default binding (verified against #63's f/g/h and F1-F3,
 * `s` stopCasting, or the numeral row itself).
 */
const EXTRA_CAST_SLOT_KEYS: readonly string[] = ["q", "e", "r", "`"];

/**
 * Number of arena/party direct-select hotkeys the default keymap reserves
 * (#63). MVP is 1v1 (one enemy, self-only party), but binding `1..3` now costs
 * nothing — an index that points at an empty seat is a runtime no-op — and it
 * reserves the keys ahead of the 3v3 roadmap so players don't have to rebind
 * when team sizes grow.
 */
const DEFAULT_DIRECT_TARGET_KEYS = 3 as const;

/**
 * The default keymap.
 *   1..8       → castSlot0..7
 *   q e r `    → castSlot8..11 (#407 — see `EXTRA_CAST_SLOT_KEYS`)
 *   Tab / Shift+Tab       → target NEAREST enemy (#815) / cycle ally target
 *   (unbound)             → cycleEnemyTarget — the seat-order enemy cycle Tab
 *                           used to do (#815 moved it off Tab; still rebindable)
 *   Escape / Shift+Escape → clear hostile / clear friendly target
 *   f g h  → arena1..3 (direct-select the Nth enemy → hostile slot)
 *   F1 F2 F3 → party1..3 (direct-select the Nth ally → friendly slot; party1 = self)
 *   s      → stopCasting (cancel the active cast/channel, #335)
 *   Space  → toggleAutoAttack (flip the #93 auto-attack on/off switch, #622)
 *   Enter  → openChat (open the in-game chat box, #833 — Enter was unbound)
 *
 * The friendly-slot actions are the Shift-modified mirror of their hostile
 * counterparts (dual targeting, #63): friendly is the MVP-secondary slot, so it
 * sits under the same keys plus Shift rather than claiming new prime keys. The
 * arena direct keys use the home-row cluster f-g-h, while party direct-select
 * uses F1-F3 (no conflict with the cast row, Tab, Escape, or s).
 *
 * The browser-reserved-key concern does not apply to F1-F3 because the game
 * owns the keyboard surface while focused.
 *
 * The arena/party direct keys use the home-row cluster f-g-h / function keys
 * (no conflict
 * with the cast row, Tab, Escape, or s; no browser-reserved keys like
 * F5/Ctrl+W). All of these live here as a const table so a future rebind UI
 * (#70) just replaces the chord values — the action set is closed and typed.
 */

/** Home-row keys for arena (enemy) direct-select `arena1..3`. */
const ARENA_KEYS: readonly string[] = ["f", "g", "h"];
/** Home-row keys for party (ally) direct-select `party1..3`. */
const PARTY_KEYS: readonly string[] = ["F1", "F2", "F3"];

export const DEFAULT_KEYMAP: Keymap = buildDefaultKeymap();

function buildDefaultKeymap(): Keymap {
  const bindings: KeyBinding[] = [];
  for (let i = 0; i < DEFAULT_CAST_SLOTS; i++) {
    // Keys "1".."8" for slots 0..7 (the numeral row); q/e/r/` for slots 8..11
    // (#407 — no single "9"/"10"/"11"/"12" key exists on a standard keyboard).
    const key = i < 8 ? String(i + 1) : EXTRA_CAST_SLOT_KEYS[i - 8];
    if (key === undefined) continue;
    bindings.push({
      action: { kind: "castSlot", slot: toAbilitySlot(i) },
      chords: [bareChord(key)],
    });
  }
  // #815: Tab selects the NEAREST enemy (was: the next enemy in seat order).
  // In anything bigger than 1v1 the seat-order cycle regularly landed on a
  // hostile across the arena, so the follow-up cast failed `outOfRange` and
  // the press read as a no-op.
  bindings.push({ action: { kind: "targetNearestEnemy" }, chords: [bareChord("Tab")] });
  // #815: the pre-#815 seat-order cycle, preserved UNCHANGED as its own
  // rebindable action — but with NO DEFAULT CHORD. Tab is the only key it ever
  // had, nearest now owns it, and silently stealing a second key for the cycle
  // would be worse than requiring one deliberate bind in the settings screen
  // (where it still appears, as an unbound row).
  bindings.push({ action: { kind: "cycleEnemyTarget" }, chords: [] });
  bindings.push({
    action: { kind: "cycleAllyTarget" },
    chords: [normalizeChord("Tab", { shift: true })],
  });
  bindings.push({ action: { kind: "clearHostile" }, chords: [bareChord("Escape")] });
  bindings.push({
    action: { kind: "clearFriendly" },
    chords: [normalizeChord("Escape", { shift: true })],
  });
  for (let i = 0; i < DEFAULT_DIRECT_TARGET_KEYS; i++) {
    const arenaKey = ARENA_KEYS[i];
    const partyKey = PARTY_KEYS[i];
    if (arenaKey !== undefined) {
      bindings.push({ action: { kind: "arenaTarget", index: i }, chords: [bareChord(arenaKey)] });
    }
    if (partyKey !== undefined) {
      bindings.push({ action: { kind: "partyTarget", index: i }, chords: [bareChord(partyKey)] });
    }
  }
  // #563: the movement-only "stop" action (click-to-move's sibling) is
  // removed outright — WASD is the only movement input now, and `s` binds
  // directly to "stopCasting" (#335).
  bindings.push({ action: { kind: "stopCasting" }, chords: [bareChord("s")] });
  // #622: spacebar toggles auto-attack on/off, default ON. `KeyboardEvent.key`
  // for the space bar is a literal single space (" "), not the string
  // "Space" — `bareChord(" ")` normalizes to that same value (a single-char
  // key is lower-cased, which is a no-op for " "); `chordLabel` renders it
  // as "Space" for display. No conflict with any other default binding.
  bindings.push({ action: { kind: "toggleAutoAttack" }, chords: [bareChord(" ")] });
  // #833: Enter opens the in-game chat box. Enter was bound to NOTHING before
  // this, so there is no conflict to resolve and no existing muscle memory to
  // break. Kept in this table (not hardcoded in the chat component) so it is
  // rebindable like every other action and shows up in `findConflicts`.
  bindings.push({ action: { kind: "openChat" }, chords: [bareChord("Enter")] });
  return { bindings };
}

/**
 * Build the `chordId → GameAction` reverse index for O(1) dispatch. First
 * binding to claim a chord id wins (deterministic, def order); later collisions
 * are ignored here and reported by `findConflicts`. Pure.
 */
export function buildReverseIndex(keymap: Keymap): ReadonlyMap<string, GameAction> {
  const index = new Map<string, GameAction>();
  for (const binding of keymap.bindings) {
    for (const chord of binding.chords) {
      const id = chordId(chord);
      if (!index.has(id)) {
        index.set(id, binding.action);
      }
    }
  }
  return index;
}

/**
 * The display label(s) for the first chord bound to `castSlot(slot)` in a
 * keymap, or `null` if the slot is unbound. Used by the action bar to show the
 * live keybind on each slot instead of a hardcoded number. v1 label is the
 * chord's key (modifiers not shown until #68). First bound chord wins.
 */
export function castSlotKeyLabel(keymap: Keymap, slot: number): string | null {
  for (const binding of keymap.bindings) {
    if (binding.action.kind === "castSlot" && (binding.action.slot as number) === slot) {
      const first = binding.chords[0];
      return first === undefined ? null : chordLabel(first);
    }
  }
  return null;
}

/**
 * The KEYBIND BADGE label for a bar position — the live bound key, or the
 * 1-based slot number when that position is unbound (#921).
 *
 * THE ONE definition of that fallback rule. `ActionBar` had it inline and
 * `LoadoutPicker`'s slot-layout preview bar had a hardcoded `slot + 1` with no
 * keymap lookup at all, so the preview showed "1..12" while the real bar the
 * player was arranging showed "1..8 q e r `" — the preview disagreed with the
 * thing it previews, and a rebind never reached it. Both call sites read this
 * now, so a badge can only ever be wrong in one place.
 *
 * Keyed by the VISUAL bar position (a keybind belongs to a bar POSITION, not
 * to a fixed ability — #417). Pure.
 */
export function castSlotBadgeLabel(keymap: Keymap, slot: number): string {
  return castSlotKeyLabel(keymap, slot) ?? String(slot + 1);
}

/**
 * The display label for the first chord bound to a DIRECT-SELECT target action
 * — `arenaTarget(index)` (faction `"arena"`) or `partyTarget(index)` (faction
 * `"party"`) — in a keymap, or `null` when that index is unbound or absent from
 * the keymap. The friendly-target (nameplate #136) mirror of `castSlotKeyLabel`:
 * same "resolve the LIVE bound key for display" contract, keyed by roster index
 * instead of ability slot. First bound chord wins; v1 label is the chord's key
 * (modifiers rendered by `chordLabel`).
 */
export function targetHotkeyKeyLabel(
  keymap: Keymap,
  faction: "arena" | "party",
  index: number,
): string | null {
  for (const binding of keymap.bindings) {
    const a = binding.action;
    // Narrow to the two index-carrying variants, then match faction + index.
    if (
      (a.kind === "arenaTarget" || a.kind === "partyTarget") &&
      a.kind === `${faction}Target` &&
      a.index === index
    ) {
      const first = binding.chords[0];
      return first === undefined ? null : chordLabel(first);
    }
  }
  return null;
}

/** A detected conflict: one chord id claimed by 2+ distinct actions. */
export interface KeymapConflict {
  readonly chordId: string;
  readonly actions: readonly GameAction[];
}

/**
 * Find chords bound to more than one DISTINCT action. Two chords of the SAME
 * action (e.g. two keys for one ability) are not a conflict. Pure; iteration
 * is def-order so the report is deterministic.
 */
export function findConflicts(keymap: Keymap): readonly KeymapConflict[] {
  const byChord = new Map<string, GameAction[]>();
  for (const binding of keymap.bindings) {
    for (const chord of binding.chords) {
      const id = chordId(chord);
      const list = byChord.get(id);
      if (list === undefined) {
        byChord.set(id, [binding.action]);
      } else {
        // Only record a distinct action once per chord.
        const already = list.some((a) => gameActionId(a) === gameActionId(binding.action));
        if (!already) list.push(binding.action);
      }
    }
  }
  const conflicts: KeymapConflict[] = [];
  for (const [id, actions] of byChord) {
    if (actions.length > 1) conflicts.push({ chordId: id, actions });
  }
  return conflicts;
}
